import { useState } from 'react';
import { AppBar, Toolbar, Typography, Container, Box, IconButton, Tooltip } from '@mui/material';
import { AlternateEmail } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ConditionalWrapper, getManagerSearchParams, isMobileApp, useMedia } from '../Utilities';
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { Link } from 'react-router-dom';
import { useKeycloak } from '../Keycloak';
import Weather from "./Weather"
import Settings from './Settings';
import Stats from './Stats';
import Support from './Support';
import Menu from './Menu';
import ArrowNavigation from './ArrowNavigation';
import AppStoreLinks from './AppStoreLinks';
import Notifications from './Notifications';

export default function Header({ onShowWhatsNew }) {

    const { t } = useTranslation()
    const { LAYOUT: { INVERTED_COLORS, TITLE } } = window.conf
    const { isPortrait } = useMobileOrientation()
    const { isManager, isMobileApp: isMobileAppViaManager } = getManagerSearchParams()
    const { logos, links } = useMedia()
    const { isLoggedIn, isUsersPortal } = useKeycloak()

    const isAuthorized = isLoggedIn() && isUsersPortal()
    const netcoColor = window.localStorage.getItem('btv_color')
    const hasArrowNavigation = isManager || isMobileApp

    const [invertColors, setInvertColors] = useState(netcoColor ? netcoColor === "true" : INVERTED_COLORS)
    const [isSupportOpen, setIsSupportOpen] = useState(false)

    const toggleInvertColors = () => {
        var toggledColors = !invertColors
        setInvertColors(toggledColors)
        window.localStorage.setItem('btv_color', toggledColors)
    }

    const handleOpenSupport = () => setIsSupportOpen(true)
    const handleCloseSupport = () => setIsSupportOpen(false)

    return (
        <AppBar 
            position="sticky" 
            sx={{ 
                zIndex: theme => isMobile && isPortrait ? theme.zIndex.drawer - 1 : theme.zIndex.drawer + 1, 
                backgroundColor: invertColors ? 'primary.main' : 'secondary.main'
            }}
        >
            <Container maxWidth={false}>
                <Toolbar 
                    disableGutters
                    variant={isMobile ? 'dense' : "regular"}
                >

                    {/* logo */}

                    {!hasArrowNavigation && (
                        <Link to={links.btv} target='_blank' rel="noopener">
                            <Box sx={{ 
                                flexGrow: 0, 
                                display: 'flex',
                                height: { xs: '16px', sm: '32px' }
                            }}>
                                <img
                                    src={invertColors ? logos.btv.light : logos.btv.dark}
                                    alt='BauTV+'
                                    height="100%"
                                />
                            </Box>
                        </Link>
                    )}

                    {/* manager controls */}

                    <ConditionalWrapper
                        condition={hasArrowNavigation}
                        wrapper={children => (
                            <ArrowNavigation invertColors={invertColors}>
                                {children}
                            </ArrowNavigation>
                        )}
                    >
                        {/* title */}

                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ 
                                flexGrow: !hasArrowNavigation && 1,
                                minWidth: 0,
                                pl: { xs: hasArrowNavigation ? 0.5 : "8px", sm: hasArrowNavigation ? 1 : "16px" },
                                pr: { xs: hasArrowNavigation ? 0.5 : 0, sm: 1 },
                                userSelect: "none",
                                fontSize: { xs: '1rem', sm: '1.33rem' },
                                color: invertColors ? 'white' : 'primary.main',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {TITLE || "Portal"}
                        </Typography>

                    </ConditionalWrapper>

                    {/* notifications */}

                    {isAuthorized && (isMobileApp || isMobileAppViaManager) && <Notifications invertColors={invertColors} />}
                    
                    {/* app store links */}

                    {!isMobileApp && !isMobileAppViaManager && <AppStoreLinks invertColors={invertColors} />}
                    
                    {/* weather */}

                    <Weather />

                    {/* statistics */}

                    {<Stats invertColors={invertColors} isAuthorized={isAuthorized} />}

                    {/* menu */}

                    <Menu invertColors={invertColors} />

                    {/* settings */}

                    <Settings
                        invertColors={invertColors}
                        toggleInvertColors={toggleInvertColors}
                        onShowWhatsNew={onShowWhatsNew}
                        isAuthorized={isAuthorized}
                    />

                    {/* support */}

                    {isAuthorized && (
                        <Tooltip title={t("support.support")} arrow>
                            <IconButton
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    p: 0,
                                    ml: "16px",
                                    mb: "1.5px",
                                    color: invertColors ? 'white' : 'primary.main'
                                }}
                                onClick={handleOpenSupport}
                            >
                                <AlternateEmail sx={{ fontSize: '1.2rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Support
                        isOpen={isSupportOpen}
                        handleClose={handleCloseSupport}
                        isAuthorized={isAuthorized}
                    />

                </Toolbar>
            </Container>
        </AppBar>
    )
}